import { h } from 'preact';
import {
	useContext, useReducer, createContext, useMemo, useEffect,
} from 'preact/compat';
import * as currency from 'currency.js';
import type { Candidate, Organization } from '../types';
import { useDonationStep, DonationStepEnum } from './step';
import { safegaurd } from './candidateSafeGaurds';

/**
 * Cart/Selections map context.
 */
export interface CartSelectionAdds {
	amount: number;
	manualInput?: boolean;
	isUpsell?: boolean;
}

export interface CartSelectionCandidate extends Candidate, CartSelectionAdds {}

export interface CartSelectionOrganization extends Organization, CartSelectionAdds {}

export type CartSelection = CartSelectionCandidate | CartSelectionOrganization;

export type CartSelectionMap = Map<number, CartSelection>;

export interface CartState {
	selections: CartSelectionMap;
	allowUserInput: boolean;
	total: number;
}

export enum CartActionType {
	TOGGLE_SELECTION = 'TOGGLE_SELECTION',
	REMOVE_SELECTION = 'REMOVE_SELECTION',
	UPDATE_SELECTION = 'UPDATE_SELECTION',
	SPLIT_AMOUNT = 'SPLIT_AMOUNT',
	SET_USER_INPUT = 'SET_USER_INPUT',
	MANUAL_INPUT = 'MANUAL_INPUT',
	CLEAR_SELECTIONS = 'CLEAR_SELECTIONS',
	UPDATE_TOTAL = 'UPDATE_TOTAL',
}

export type CartReducerAction = {
	type: CartActionType.REMOVE_SELECTION,
	payload: {
		id: number,
	}
} | {
	type: CartActionType.TOGGLE_SELECTION,
	payload: CartSelection
} | {
	type: CartActionType.UPDATE_SELECTION,
	payload: CartSelection
} | {
	type: CartActionType.CLEAR_SELECTIONS,
} | {
	type: CartActionType.MANUAL_INPUT,
	payload: CartSelection
} | {
	type: CartActionType.UPDATE_TOTAL,
	payload: number
} | {
	type: CartActionType.SPLIT_AMOUNT,
	payload: {
		candidates: Candidate[],
		amount: number,
	}
} | {
	type: CartActionType.SET_USER_INPUT,
	payload: boolean,
}

type CartAction = (action: CartReducerAction) => void;

export const Cart = createContext<{
	cart: CartState,
	dispatch: CartAction
} | undefined>(undefined);

/**
 * Normalize currency value.
 * Sets defaults for currency.js object.
 */
export const normalizeCurrency = (amount: number|string): currency => currency(
	amount,
	{
		separator: ',',
		decimal: '.',
		precision: 2,
	},
);

/**
 * Normalize and return formatted currency string.
 * Preserves decimals if present, otherwise truncates.
 */
export const formatCurrency = (amount: number|string): string => {
	const f = normalizeCurrency(amount).format();
	return parseInt(f.slice(-2), 10) > 0 ? f : f.substring(0, f.length - 3);
};

/**
 * Specifically used for button inputs.
 */
const toggleSelection = (state: CartState, action: { type: string; payload: CartSelection }) => {
	const { payload } = action;

	if (!payload.id || !safegaurd(payload)) {
		return state;
	}
	const { selections } = state;
	const current = selections.get(payload.id);
	// Normalize currency value.
	payload.amount = normalizeCurrency(payload.amount).value;
	/**
	 * If the selection is already in the cart and amount is the same, then remove it.
	 * If current was a manual input or not in the cart, then add it with manualInput set to false.
	 */
	if (current && current.amount === payload.amount && !current.manualInput) {
		selections.delete(payload.id);
	} else {
		selections.set(payload.id, {
			...payload,
			manualInput: false,
		});
	}
	return {
		...state,
		selections,
	};
};

/**
 * Remove a selection from the cart.
 */
const removeSelection = (state: CartState, action: { type: string; payload: { id: number }}) => {
	const { payload } = action;
	if (!payload.id) {
		return state;
	}
	const { selections } = state;
	// Warn if the selection is not in the cart.
	if (!selections.delete(payload.id)) {
		console.log(`Skip removing non-selected id: ${payload.id}`);
	}
	return {
		...state,
		selections,
	};
};

/**
 * Handles both manual input and generic select.
 */
const updateSelection = (
	state: CartState,
	action: { type: string; payload: CartSelection },
	manualInput = false,
) => {
	const { payload } = action;
	if (!payload.id || !safegaurd(payload)) {
		return state;
	}

	// Normalize currency value.
	payload.amount = normalizeCurrency(payload.amount).value;
	const { selections } = state;
	selections.set(payload.id, {
		...payload,
		manualInput,
	});
	return {
		...state,
		selections,
	};
};

/**
 * Clear all selections.
 */
const clearSelections = (state: CartState) => ({
	...state,
	selections: new Map(),
});

/**
 * Update total.
 */
const updateTotal = (state: CartState, action: { type: string; payload: number }) => {
	const { payload } = action;
	return {
		...state,
		total: payload,
	};
};

/**
 * Split Amount.
 */
const splitAmount = (
	state: CartState,
	action: { type: string; payload: { candidates: Candidate[]; amount: number } },
) => {
	const { payload } = action;
	const { candidates, amount } = payload;
	const useableCandidates = candidates.filter((c) => safegaurd(c) && c.id);

	// Normalize currency value.
	const split = normalizeCurrency(amount).divide(useableCandidates.length).value;
	const selections = new Map();

	useableCandidates.forEach((candidate) => {
		selections.set(candidate.id, {
			...candidate,
			amount: split,
			manualInput: true,
		});
	});

	return {
		...state,
		selections,
	};
};

/**
 * Set user input.
 * This determines if user selected value inputs are allowed.
 * Usually used in conjunction with toggling off split amount.
 */
const setUserInput = (
	state: CartState,
	action: { type: string; payload: boolean },
) => {
	const { payload } = action;
	return {
		...state,
		allowUserInput: payload,
	};
};

/**
 *  Cart Reducer Actions.
 */
const cartReducer = (state: CartState, action: CartReducerAction) => {
	/**
	 * Cart Actions.
	 */
	switch (action.type) {
	case 'TOGGLE_SELECTION':
		return toggleSelection(state, action);
	case 'REMOVE_SELECTION':
		return removeSelection(state, action);
	case 'UPDATE_SELECTION':
		return updateSelection(state, action, false);
	case 'CLEAR_SELECTIONS':
		return clearSelections(state);
	case 'MANUAL_INPUT':
		return updateSelection(state, action, true);
	case 'UPDATE_TOTAL':
		return updateTotal(state, action);
	case 'SPLIT_AMOUNT':
		return splitAmount(state, action);
	case 'SET_USER_INPUT':
		return setUserInput(state, action);
	default:
		return state;
	}
};

/**
 * Cart Provider.
 */
const CartProvider = ({ children }: { children: h.JSX.Element }) => {
	const [cart, dispatch] = useReducer(cartReducer, {
		selections: new Map(),
		total: 0,
		allowUserInput: true,
	});

	/**
	 * Effect controls:
	 * - update cart total
	 * - on total update ensure valid value,
	 * if invalid force step to entry.
	 */
	const step = useDonationStep();
	useEffect(() => {
		const { selections } = cart;
		let total = normalizeCurrency(0);
		selections.forEach((selection) => {
			// Immutable, replace total with new object.
			total = total.add(selection.amount);
		});

		// Update has occurred.
		if (total.value !== cart.total) {
			dispatch({ type: CartActionType.UPDATE_TOTAL, payload: total.value });
			if (step.state !== DonationStepEnum.ENTRY && total.value === 0) {
				step.dispatch({
					type: 'MANUAL_STEP',
					payload: {
						step: DonationStepEnum.ENTRY,
					},
				});
			}
		}
	}, [cart, step.state]);

	const cartContext = useMemo(() => ({
		cart,
		dispatch,
	}), [cart]);

	return (
		<Cart.Provider value={cartContext}>
			{children}
		</Cart.Provider>
	);
};

const useCart = () => {
	const context = useContext(Cart);
	if (context === undefined) {
		throw new Error('useCart must be used within a CartProvider');
	}
	return context;
};

export { CartProvider, useCart };
